import { useTranslation } from "react-i18next";
import { useProductDescriptorContext } from "~/domains/product/ProductContext";
import { Accordion, AccordionDetails, AccordionSummary, Card, Divider } from "~/common/mui";
import {
  AliasesParam,
  CountryOfOriginDescriptorParam,
  OldDesignationParam,
  SchaefflerIdParam,
  TariffParam,
} from "../ProductParamsV3";
import type { ProductDescriptor } from "../../types/ProductDescriptor";
import {
  shouldRenderSchaefflerIdParam,
  shouldRenderAliasesParam,
  shouldRenderTariffParam,
  shouldRenderCountryOfOriginDescriptorParam,
} from "../ProductParamsV3";
import { shouldRenderOldDesignationParam } from "../ProductParamsV3/params";
import type { ReactNode } from "react";

export interface CommercialDetailsProps {
  defaultExpanded?: boolean;
}

export const hasCommercialDetails = (productDescriptor?: ProductDescriptor): boolean => {
  return (
    shouldRenderOldDesignationParam(productDescriptor?.base) ||
    shouldRenderSchaefflerIdParam(productDescriptor) ||
    shouldRenderAliasesParam({ product: productDescriptor?.base }) ||
    shouldRenderTariffParam(productDescriptor?.base) ||
    shouldRenderCountryOfOriginDescriptorParam({ descriptor: productDescriptor })
  );
};

export const CommercialDetails = ({ defaultExpanded = true }: CommercialDetailsProps) => {
  const productDescriptor = useProductDescriptorContext();

  if (!hasCommercialDetails(productDescriptor)) {
    return null;
  }

  return (
    <Shell defaultExpanded={defaultExpanded}>
      <OldDesignationParam />
      <SchaefflerIdParam />
      <AliasesParam />
      <TariffParam />
      <CountryOfOriginDescriptorParam />
    </Shell>
  );
};

const Shell = ({ children, defaultExpanded }: CommercialDetailsProps & { children?: ReactNode }) => {
  const { t } = useTranslation();

  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      component={Card}
      sx={{ bgcolor: "surface.bright", containerType: "inline-size" }}
    >
      <AccordionSummary sx={{ fontWeight: "normal" }}>
        {t("product.commercialDetails.title", "Commercial details")}
      </AccordionSummary>
      <Divider flexItem />
      <AccordionDetails
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "1fr",
          wordBreak: "break-all",
          "@container (min-width: 300px)": {
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          },
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

CommercialDetails.Shell = Shell;
